import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import '../../../css/FactSheets.css';
import { Introduction } from '../Components/Introduction';
import { TextBlock } from '../Components/TextBlock';
import { ContactUsBlock } from '../Components/ContactUsBlock';
import sabine_supermarket from '../../../Assets/images/sabine_supermarket.png';
import './WhatIsPlb.css';
import plb_product_icon from '../../../Assets/icons/plb_product_icon.png';
import plb_eftpos_icon from '../../../Assets/icons/plb_eftpos_icon.png';
import plb_customer_icon from '../../../Assets/icons/plb_customer_icon.png';
import plb_choices_icon from '../../../Assets/icons/plb_shopping_success_icon.svg';
import plb_checkmark_icon from '../../../Assets/icons/plb_checkmark_icon.svg';
import plb_protected_icon from '../../../Assets/icons/plb_protected_icon.svg';
import plb_pos_img from '../../../Assets/images/plb_pos_screen-EIM.svg'
import { Helmet } from 'react-helmet';
import { PageTitles } from '../../../utils/PageTitles';
import { useNavigate } from 'react-router-dom';
import { CompatibilityLocation } from '../../../routeConstants';
import ApiService from '../../../Services/apiServices';
import {AxiosResponse} from 'axios';
import { FactSheetServerErrorMessage } from '../FactSheetErrorMessage/FactSheetErrorMessage';

export const WhatIsPlb = () => {
    const [storageUrl, setStorageUrl] = React.useState<string>('');
    const [updatedDateDescription, setUpdatedDateDescription] = React.useState<string>('');
    const [apiError, setApiError] = React.useState<boolean>(false);

    const navigate = useNavigate();
    
    const goToLocation = useCallback(
        (location: string) => (e: React.MouseEvent<HTMLSpanElement>) => {
            globalThis.scrollTo(0, 0);
            navigate(location);
        },
        [navigate],
    )
    
    const GoToLocationOnEnter = useCallback(
        (location: string) => (e: React.KeyboardEvent<HTMLSpanElement>) => {
            if(e.key === "Enter") {
                globalThis.scrollTo(0, 0);
                navigate(location);
            }
        },
        [navigate],
    )

   useEffect(() => {
        fetchMetaData();
      }, []);

    // fetches the document's url to download from and the date description to display from the API
    // and then applies it to the page.  If it can't be found, an error message is displayed
    // next to the button and the error logged to the browser's console.
    const fetchMetaData = () => {
        ApiService.getFactSheetMetadata('What_Is_Product_Level_Blocking')
        .then((response : AxiosResponse) => {
            setApiError(false);
            setStorageUrl(response.data.value[0].StorageUrl);
            setUpdatedDateDescription(response.data.value[0].UpdatedDateDescription);
        })
        .catch((e) => {
            console.log('Error fetching factsheet metadata for What is product level blocking');
            console.error(e);
            setApiError(true);
            setUpdatedDateDescription(FactSheetServerErrorMessage);
        });
    };

    const heading1 = {
        start: "What is ",
        highlight: "Product Level Blocking?",
        body: <>
                Product Level Blocking is a function on the point of sale (POS) system to ensure restricted items cannot be purchased with a SmartCard.
                <br/><br/>The POS system and EFTPOS terminal work together to identify when a SmartCard is presented by a customer, and if there are any restricted items in the sale.
            </>
    }

    /* https://stackoverflow.com/questions/55663491/how-to-detect-screen-orientation-in-mobile-website-when-using-react-js-and-orien*/
    const getOrientation = () => window.screen.orientation.type
    const useScreenOrientation = () => {
        const [orientation, setOrientation] = useState(getOrientation())
        const updateOrientation = (event: Event) => {
            setOrientation(getOrientation())
        }
    
        useEffect(() => {
            window.addEventListener('orientationchange', updateOrientation)
            return () => { 
                window.removeEventListener('orientationchange', updateOrientation)
            }
        }, [])
        return orientation
    }

    return(
        <>
            <Helmet>
                <title>{PageTitles.WhatIsProductLevelBlocking} </title>
            </Helmet>
            <Introduction
                heading1 = {heading1}
                downloadButton = {true}                
                storageUrl={storageUrl}
                updatedDateDescription={updatedDateDescription}
                apiError={apiError}
            />
            <TextBlock
                variant = "violet"
                heading = "How does Product Level Blocking work?"
                content = {<>
                        <Grid container className="plb_gridContainer">

                            {/*******************************************************************************/}
                            <Grid item  xs={12}
                                        lg ={useScreenOrientation().startsWith('portrait') ? 12 : 4} 
                                        className="plb_diagramLeftSideGrid">

                                    <div className="plb_diagramSideContainer">

                                        {/* 1. EFTPOS terminal */}
                                        <div className="plb_diagramSideItemContainer">
                                            <Box className="plb_diagramBox">
                                                <Typography className="plb_diagramNumber">1</Typography>
                                                <div className="plb_diagramBody">
                                                    <Typography align="left"><strong>EFTPOS terminal</strong></Typography>
                                                    <Typography align="left">identifies SmartCard</Typography>
                                                </div>
                                                <div className="plb_diagramImageBox">
                                                    <img src={plb_eftpos_icon} className="plb_diagramIcon" alt=""/>
                                                </div>
                                            </Box>
                                            <Box className="plb_diagramLine"></Box>
                                        </div>

                                        {/* 2. Restricted Items */}
                                        <div className="plb_diagramSideItemContainer">
                                            <Box className="plb_diagramBox">
                                                <Typography className="plb_diagramNumber">2</Typography>
                                                <div className="plb_diagramBody">
                                                    <Typography align="left"><strong>Restricted items</strong></Typography>
                                                    <Typography align="left">displayed to the POS operator</Typography>
                                                </div>
                                                <div className="plb_diagramImageBox">
                                                    <img src={plb_product_icon} className="plb_diagramIcon" alt=""/>
                                                </div>
                                            </Box>
                                            <Box className="plb_diagramLine"></Box>
                                        </div>

                                    </div>

                            </Grid>

                            {/*******************************************************************************/}
                            {/* Central image EFTPOS terminal*/}
                            <Grid item  xs={12} 
                                        lg={useScreenOrientation()?.startsWith('portrait') ? 8 : 4} 
                                        className="plb_diagramCenterGrid">

                                <Box className="plb_diagramCenterBox">
                          
                                        <Typography variant="h2" className="plb_diagramHeading plb_diagramCenterHeading">
                                            Point of Sale (POS) system
                                        </Typography>

                                        <div className="plb_diagramCenterImageBox">
                                            <img src={plb_pos_img} className="plb_diagramCenterImage" alt="The POS system displays restricted and non-restricted items"/>
                                        </div>
                                
                                </Box>
                            </Grid>

                            {/*******************************************************************************/}
                           
                            <Grid item  xs={12} 
                                        lg={useScreenOrientation()?.startsWith('portrait') ? 12 : 4} 
                                        className="plb_diagramRightSideGrid">
                                
                                <div className="plb_diagramSideContainer">

                                    {/* 3. Customer */}
                                    <div className="plb_diagramSideItemContainer">
                                        <Box className="plb_diagramLine"></Box>
                                        <Box className="plb_diagramBox">
                                            <Typography className="plb_diagramNumber">3</Typography>
                                            <div className="plb_diagramBody">
                                                <Typography align="left"><strong>Customer</strong></Typography>
                                                <Typography align="left">chooses what to do with any restricted items</Typography>
                                            </div>
                                            <div className="plb_diagramImageBox">
                                                <img src={plb_customer_icon} className="plb_diagramIcon" alt=""/>
                                            </div>                 
                                        </Box>
                                    </div>

                                </div>
                            </Grid>


                        </Grid>

                        {/*******************************************************************************/}
                        {/* Benefits */}
                        <Grid container sx={{mt:'1.875rem', width:'100%'}} className="plb_diagramBottomGrid">

                            <Grid item className="plb_diagramBottom">

                                    <div className="plb_diagramBottomOuterContainer">
                                        
                                        {/* Protects */}
                                        <Box className="plb_diagramBottomItem plb_diagramBottomItemLeft" >
                                            <div className="plb_diagramBottomContainer plb_diagramBottomDivider">
                                                <img src={plb_protected_icon} className="plb_diagramIcon" alt=""/>
                                                <div className="plb_diagramBottomBody">
                                                    <Typography align="left"><strong>Protects the customer</strong></Typography>
                                                    <Typography align="left">by identifying restricted products at the point of sale.</Typography>
                                                </div>
                                            </div>
                                        </Box>

                                      {/* Choice */}
                                        <Box className="plb_diagramBottomItem plb_diagramBottomItemMiddle">
                                            <div className="plb_diagramBottomContainer plb_diagramBottomDivider">
                                                <img src={plb_choices_icon} className="plb_diagramIcon" alt=""/>
                                                <div className="plb_diagramBottomBody">
                                                    <Typography align="left"><strong>Empowers choice</strong></Typography>
                                                    <Typography align="left">by giving the customer options for restricted items.</Typography>
                                                </div>
                                            </div>
                                        </Box>
                                        
                                        {/* Complies */ }
                                        <Box className="plb_diagramBottomItem plb_diagramBottomItemRight">
                                            <div className="plb_diagramBottomContainer plb_diagramBottomDividerNone">
                                            <img src={plb_checkmark_icon} className="plb_diagramIcon" alt=""/>
                                                <div className="plb_diagramBottomBody">
                                                    <Typography align="left"><strong>Ensures compliance</strong></Typography>
                                                    <Typography align="left">while enabling a smooth and respectful checkout experience.</Typography>
                                                </div>
                                            </div>
                                        </Box>

                                    </div>

                            </Grid>   
                        
                        </Grid>

                </>}
            />
            <TextBlock
                variant = "white"
                heading = "Why would I want Product Level Blocking in my business?"
                body = {<p style={{height: '100%'}}>By having Product Level Blocking in your business:
                    <ul style={{height: '100%'}}>
                        <li>you can easily accept the SmartCard as a form of payment if you sell any restricted items</li>
                        <li>your POS system will automatically prevent the sale of restricted items</li>
                        <li>your staff do not need to recognise the SmartCard or remember which items are restricted</li>
                        <li>you can help your customers manage their money.</li>
                    </ul></p>}
            />
            <TextBlock
                variant = "violet"
                heading = "How to get Product Level Blocking"
                body = {<p style={{height: '100%'}}>Getting Product Level Blocking is an easy, two-step process:
                        <ol style={{height: '100%'}}>
                            <li>Do the simple <span className='section_1_highlight' tabIndex={0} role="link" onKeyDown={GoToLocationOnEnter(CompatibilityLocation.path)} onClick={goToLocation(CompatibilityLocation.path)}>eligibility check</span>.</li>
                            <li>Then follow the prompts to read and sign a Merchant Participation Agreement.</li>
                        </ol>
                        Your point of sale (POS) provider will then contact you to confirm a time to implement Product Level Blocking in your business.</p>}
            />
            <Box className="facts_background whiteBackground">
                <div className="facts_contentContainer" >
                    <Grid container columnSpacing={3} className="plb_quoteContainer">
                        <Grid item xs={12} sm={12} md={6}>
                            <div className="plb_quoteImageContainer">
                                <img src={sabine_supermarket} className="plb_quoteImage" alt="The checkout at Sabine Supermarket"/>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>

                            <div className="plb_quoteInnerContainer">

                                <Typography className="plb_quoteHeading" sx={{mb:'1.25rem'}}>
                                    Leo from Sabine Supermarket says:
                                </Typography>

                                <Typography className="plb_quoteMark plb_quoteStart">
                                        “
                                </Typography>
                                <Typography className="plb_quoteBody">
                                        "Very convenient and easy for check-out staff."
                                </Typography>
                                <Typography className="plb_quoteMark plb_quoteEnd">
                                        „
                                </Typography>

                            </div>
                        </Grid>
                    </Grid>
                </div>
            </Box>

            <ContactUsBlock
                variant = "violet"
            />
        </>
    );

}
